import { useState, useRef } from "react";
import { PRIORITY_COLOR, STAGE_CFG } from "./constants";
import { S, Ic, Avatar, StagePill, TagChip, ProbBar, Dropdown, fmt } from "./ui";

// ── helpers ──
const TH = ({ label, w, sort, cur, onSort }) => (
  <th onClick={() => onSort && onSort(label)} style={{ padding: "8px 10px", fontSize: "10px", fontWeight: "700", color: cur === label ? "#3761E9" : "#9898b8", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #ecedf2", whiteSpace: "nowrap", width: w, cursor: onSort ? "pointer" : "default", background: cur === label ? "#f4f7fe" : "transparent", userSelect: "none" }}>
    {label}{cur === label && " ↓"}
  </th>
);
const TD = ({ children, style: x, onClick }) => (
  <td style={{ padding: "9px 10px", borderBottom: "1px solid #f0f0f8", verticalAlign: "middle", ...x }} onClick={onClick}>{children}</td>
);
const Empty = ({ icon, title, subtitle, cols }) => (
  <tr><td colSpan={cols} style={{ textAlign: "center", padding: "52px 20px", color: "#c0c0d4" }}>
    <div style={{ fontSize: "32px", marginBottom: "10px" }}>{icon}</div>
    <div style={{ fontSize: "13px", fontWeight: "600", color: "#9898b8", marginBottom: "4px" }}>{title}</div>
    <div style={{ fontSize: "11.5px", color: "#c0c0d4" }}>{subtitle}</div>
  </td></tr>
);

const PriorityDot = ({ o }) => o.priority
  ? <span style={{ ...S.row(3), fontSize: "11.5px", color: PRIORITY_COLOR[o.priority], fontWeight: "600" }}>
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: PRIORITY_COLOR[o.priority], display: "inline-block" }} />
      {o.priority}
    </span>
  : <span style={{ color: "#ccc" }}>—</span>;

const OwnerChip = ({ o }) => o.assignee
  ? <div style={S.row(5)}><Avatar user={o.assignee} size={20} showDot /><span style={{ fontSize: "11.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.assignee.name}</span></div>
  : <span style={{ fontSize: "11px", color: "#c0c0d8" }}>—</span>;

const TagsChips = ({ o }) => (
  <div style={{ ...S.row(3, true) }}>
    {o.tags?.slice(0, 1).map((tag, i) => <TagChip key={i} tag={tag} sm />)}
    {o.tags?.length > 1 && <span style={{ fontSize: "9.5px", color: "#9e9eb8" }}>+{o.tags.length - 1}</span>}
  </div>
);

// ── CSV Export ──
function exportCSV(opps) {
  const headers = ["Created","Name","Source","Budget","Stage","Contact","Company","Owner","Tags","Repeat Customer","Best Contact Method","Start Date","Close Date","Notes"];
  const rows = opps.map(o => [
    new Date().toLocaleString("en-US"),
    o.name || "",
    o.source || "",
    o.amount || "",
    o.stage || "",
    o.contact || "",
    o.company || "",
    o.assignee?.name || "",
    (o.tags || []).map(t => t.label).join("; "),
    o.repeatCustomer ? "Yes" : "No",
    o.contactMethod || "",
    o.startDate || "",
    o.endDate || "",
    (o.notes || "").replace(/"/g, '""'),
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
  a.download = `opportunities_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
}

// ── List Settings Panel ──
const ALL_COLUMNS = [
  "Name","Source","Budget","Stage","Priority","Contact","Company","Owner","Tags","Repeat Customer","Best Contact Method","Start Date","Close Date","Notes","Probability"
];

function ListSettingsPanel({ visible, cols, setCols, onClose }) {
  const [tab, setTab] = useState("columns");
  const [viewMode, setViewMode] = useState("classic");
  if (!visible) return null;
  const toggle = c => setCols(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(0,0,0,0.2)" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ position: "absolute", right: "20px", top: "100px", width: "340px", background: "#fff", borderRadius: "14px", boxShadow: "0 8px 30px rgba(0,0,0,0.15)", overflow: "hidden", animation: "fadeIn .15s ease" }}>
        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #ecedf2", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "13.5px", fontWeight: "700", color: "#1a1a2e" }}>List Settings</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", color: "#a0a0b8", fontSize: "16px" }}>×</button>
        </div>
        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #ecedf2" }}>
          {["columns","view"].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "10px", border: "none", background: "transparent", cursor: "pointer", fontSize: "12px", fontWeight: tab === t ? "700" : "400", color: tab === t ? "#3761E9" : "#8888a8", borderBottom: tab === t ? "2px solid #3761E9" : "2px solid transparent", textTransform: "capitalize" }}>
              {t === "columns" ? "Columns" : "View"}
            </button>
          ))}
        </div>
        {/* Content */}
        <div style={{ padding: "14px 18px", maxHeight: "340px", overflowY: "auto" }}>
          {tab === "columns" && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                <button onClick={() => setCols([...ALL_COLUMNS])} style={{ fontSize: "11px", color: "#3761E9", fontWeight: "600", background: "none", border: "none", cursor: "pointer" }}>Select All</button>
                <button onClick={() => setCols(["Name"])} style={{ fontSize: "11px", color: "#E53935", fontWeight: "600", background: "none", border: "none", cursor: "pointer" }}>Discard All</button>
              </div>
              {ALL_COLUMNS.map(c => (
                <label key={c} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "7px 0", cursor: "pointer", borderBottom: "1px solid #f5f5fb" }}>
                  <input type="checkbox" checked={cols.includes(c)} onChange={() => toggle(c)} style={{ accentColor: "#3761E9", width: "14px", height: "14px" }} />
                  <span style={{ fontSize: "12.5px", color: "#2b3674", fontWeight: "500" }}>{c}</span>
                </label>
              ))}
            </>
          )}
          {tab === "view" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {[{k:"compact",l:"Compact view"},{k:"classic",l:"Classic view"},{k:"list",l:"List view"}].map(({k,l}) => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", borderRadius: "8px", cursor: "pointer", background: viewMode === k ? "#f0f4ff" : "#f8f8fc", border: `1px solid ${viewMode === k ? "#3761E9" : "#e0e0e8"}` }}>
                  <input type="radio" checked={viewMode === k} onChange={() => setViewMode(k)} style={{ accentColor: "#3761E9" }} />
                  <span style={{ fontSize: "12.5px", fontWeight: viewMode === k ? "700" : "400", color: viewMode === k ? "#3761E9" : "#3a3a5c" }}>{l}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Summary Bar ──
function SummaryBar({ opps, activeTab }) {
  const shown = activeTab === "archived" ? opps.filter(o => o.archived) : activeTab === "all" ? opps : opps.filter(o => !o.archived);
  const total = shown.length;
  const value = shown.reduce((s, o) => s + (Number(o.amount) || 0), 0);
  const won   = shown.filter(o => o.stage === "Closed Won").length;
  const avgProb = total ? Math.round(shown.reduce((s, o) => s + (Number(o.probability) || 0), 0) / total) : 0;
  const Chip = ({ label, val, color }) => (
    <div style={{ background: "#fff", border: "1px solid #ecedf2", borderRadius: "10px", padding: "7px 14px", minWidth: "100px" }}>
      <div style={{ fontSize: "16px", fontWeight: "800", color, letterSpacing: "-0.5px" }}>{val}</div>
      <div style={{ fontSize: "10px", color: "#9898b8" }}>{label}</div>
    </div>
  );
  return (
    <div style={{ display: "flex", gap: "7px", marginBottom: "10px", flexWrap: "wrap", alignItems: "center" }}>
      <Chip label="Opportunities"   val={total}                                                            color="#0f3460" />
      <Chip label="Total Value"     val={"$" + value.toLocaleString("en-US", { maximumFractionDigits: 0 })} color="#2e7d32" />
      <Chip label="Won"             val={won}                                                               color="#00897b" />
      <Chip label="Avg Probability" val={avgProb + "%"}                                                     color="#7b1fa2" />
    </div>
  );
}

// ── Active Tab ──
const BASE_COLS = ["Name","Stage","Priority","Budget","Prob.","Owner","Tags","Source","Close Date"];

function ActiveTab({ opps, cols, onRowClick, onEdit, onDelete, onArchive }) {
  const [hov, setHov] = useState(null);
  const [sort, setSort] = useState("");
  const sorted = [...opps].sort((a, b) => {
    if (sort === "Name")   return a.name.localeCompare(b.name);
    if (sort === "Budget") return (b.amount || 0) - (a.amount || 0);
    if (sort === "Stage")  return a.stage.localeCompare(b.stage);
    return 0;
  });
  const showCols = BASE_COLS.filter(c => cols.includes(c));
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
        <thead>
          <tr style={{ background: "#f8f8fc" }}>
            {showCols.map(c => <TH key={c} label={c} cur={sort} onSort={setSort} />)}
            {cols.includes("Repeat Customer") && <TH label="Repeat Customer" cur={sort} onSort={setSort} />}
            {cols.includes("Best Contact Method") && <TH label="Best Contact" cur={sort} onSort={setSort} />}
            <th style={{ width: "32px" }} />
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0
            ? <Empty icon="📋" title="No active opportunities" subtitle="Create your first opportunity" cols={showCols.length + 1} />
            : sorted.map(o => (
              <tr key={o.id} style={{ background: hov === o.id ? "#f8f8fd" : "#fff", cursor: "pointer", transition: "background .1s" }}
                onMouseEnter={() => setHov(o.id)} onMouseLeave={() => setHov(null)} onClick={() => onRowClick(o)}>
                {showCols.includes("Name") && (
                  <TD><div style={{ fontSize: "12.5px", fontWeight: "700", color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}>{o.name}</div>
                    {o.activities && <div style={{ ...S.row(2), fontSize: "10px", color: "#0f3460", marginTop: "1px" }}><Ic n="activity" s={8} c="#0f3460" />{o.activities}</div>}
                  </TD>
                )}
                {showCols.includes("Stage")    && <TD><StagePill stage={o.stage} sm /></TD>}
                {showCols.includes("Priority") && <TD><PriorityDot o={o} /></TD>}
                {showCols.includes("Budget")   && <TD style={{ fontSize: "12.5px", fontWeight: "700", color: "#1a1a2e" }}>{fmt(o.amount)}</TD>}
                {showCols.includes("Prob.")    && <TD>{o.probability != null && <><ProbBar value={o.probability} height={3} /><span style={{ fontSize: "10px", color: "#8888a8" }}>{o.probability}%</span></>}</TD>}
                {showCols.includes("Owner")    && <TD><OwnerChip o={o} /></TD>}
                {showCols.includes("Tags")     && <TD><TagsChips o={o} /></TD>}
                {showCols.includes("Source")   && <TD style={{ fontSize: "11.5px", color: "#6e6e80" }}>{o.source || "—"}</TD>}
                {showCols.includes("Close Date") && <TD style={{ fontSize: "11.5px", color: "#6e6e80" }}>{o.endDate || "—"}</TD>}
                {cols.includes("Repeat Customer") && <TD style={{ textAlign: "center" }}>{o.repeatCustomer ? "✅" : "❌"}</TD>}
                {cols.includes("Best Contact Method") && <TD style={{ fontSize: "11px", color: "#6e6e80" }}>{o.contactMethod || "—"}</TD>}
                <TD onClick={e => e.stopPropagation()}>
                  <Dropdown right
                    trigger={<button style={{ width: "22px", height: "22px", border: "none", background: hov === o.id ? "#f0f0f8" : "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#b0b0c8", borderRadius: "5px" }}><Ic n="moreV" s={11} /></button>}
                    items={[
                      { label: "View", icon: "user",    onClick: () => onRowClick(o) },
                      { label: "Edit", icon: "edit",    onClick: () => onEdit?.(o)   },
                      "---",
                      { label: "Archive", icon: "archive", onClick: () => onArchive?.(o.id) },
                      { label: "Delete",  icon: "trash",   onClick: () => onDelete?.(o.id), danger: true },
                    ]}
                  />
                </TD>
              </tr>
            ))
          }
        </tbody>
      </table>
      {/* Footer row */}
      <div style={{ padding: "8px 10px", fontSize: "11px", color: "#9898b8", borderTop: "1px solid #f0f0f8", background: "#fafafa", borderRadius: "0 0 12px 12px" }}>
        Showing 1 to {sorted.length} of {sorted.length} rows
        &nbsp;·&nbsp;
        <span style={{ fontWeight: "700", color: "#2e7d32" }}>
          Total Budget: ${sorted.reduce((s, o) => s + (Number(o.amount) || 0), 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
        </span>
      </div>
    </div>
  );
}

// ── Archived Tab ──
function ArchivedTab({ opps, onRowClick, onRestore, onDelete }) {
  const [hov, setHov] = useState(null);
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
        <thead>
          <tr style={{ background: "#fdf8f0" }}>
            {["Name","Company","Stage","Budget","Owner","Source","Close Date","Archived On",""].map((l, i) => <TH key={i} label={l} />)}
          </tr>
        </thead>
        <tbody>
          {opps.length === 0
            ? <Empty icon="🗄️" title="No archived opportunities" subtitle="Archived opportunities will appear here" cols={9} />
            : opps.map(o => (
              <tr key={o.id} style={{ background: hov === o.id ? "#fdf7ee" : "#fff", cursor: "pointer", transition: "background .1s", opacity: 0.88 }}
                onMouseEnter={() => setHov(o.id)} onMouseLeave={() => setHov(null)} onClick={() => onRowClick(o)}>
                <TD><div style={{ fontSize: "12.5px", fontWeight: "700", color: "#5a5a7a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name}</div><span style={{ fontSize: "9.5px", background: "#fff3e0", color: "#e65100", borderRadius: "4px", padding: "1px 4px", fontWeight: "600" }}>Archived</span></TD>
                <TD style={{ fontSize: "11.5px", color: "#7a7a9a" }}>{o.company || "—"}</TD>
                <TD><StagePill stage={o.stage} sm /></TD>
                <TD style={{ fontSize: "12.5px", fontWeight: "700", color: "#7a7a9a" }}>{fmt(o.amount)}</TD>
                <TD><OwnerChip o={o} /></TD>
                <TD style={{ fontSize: "11.5px", color: "#9a9ab0" }}>{o.source || "—"}</TD>
                <TD style={{ fontSize: "11.5px", color: "#9a9ab0" }}>{o.endDate || "—"}</TD>
                <TD style={{ fontSize: "11px", color: "#b0b0c8" }}>{o.archivedAt || "—"}</TD>
                <TD onClick={e => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button onClick={() => onRestore?.(o)} style={{ display: "flex", alignItems: "center", gap: "3px", height: "24px", padding: "0 8px", border: "1.5px solid #e0e0e8", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "11px", color: "#0f3460", fontWeight: "600" }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#e8f0fd"; e.currentTarget.style.borderColor = "#0f3460"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e0e0e8"; }}>
                      ↩ Restore
                    </button>
                    <button onClick={() => onDelete?.(o.id)} style={{ width: "24px", height: "24px", border: "1.5px solid #fde8e8", borderRadius: "6px", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#fde8e8"}
                      onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                      <Ic n="trash" s={10} c="#e53935" />
                    </button>
                  </div>
                </TD>
              </tr>
            ))
          }
        </tbody>
      </table>
    </div>
  );
}

// ── All Tab ──
function AllTab({ opps, cols, onRowClick, onEdit, onDelete, onArchive, onRestore }) {
  const [hov, setHov] = useState(null);
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
        <thead>
          <tr style={{ background: "#f8f8fc" }}>
            <TH label="Name" /><TH label="Status" /><TH label="Company" /><TH label="Stage" />
            <TH label="Priority" /><TH label="Budget" /><TH label="Owner" /><TH label="Tags" />
            <TH label="Source" /><TH label="Close Date" />
            {cols.includes("Repeat Customer") && <TH label="Repeat" />}
            {cols.includes("Best Contact Method") && <TH label="Contact Method" />}
            <th style={{ width: "32px" }} />
          </tr>
        </thead>
        <tbody>
          {opps.length === 0
            ? <Empty icon="🔍" title="No opportunities found" subtitle="Try adjusting your filters" cols={11} />
            : opps.map(o => (
              <tr key={o.id} style={{ background: hov === o.id ? "#f8f8fd" : "#fff", cursor: "pointer", transition: "background .1s", opacity: o.archived ? 0.78 : 1 }}
                onMouseEnter={() => setHov(o.id)} onMouseLeave={() => setHov(null)} onClick={() => onRowClick(o)}>
                <TD><div style={{ fontSize: "12.5px", fontWeight: "700", color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "180px" }}>{o.name}</div></TD>
                <TD>{o.archived ? <span style={{ fontSize: "10.5px", background: "#fff3e0", color: "#e65100", borderRadius: "20px", padding: "2px 7px", fontWeight: "600" }}>Archived</span> : <span style={{ fontSize: "10.5px", background: "#e8f5e9", color: "#2e7d32", borderRadius: "20px", padding: "2px 7px", fontWeight: "600" }}>Active</span>}</TD>
                <TD style={{ fontSize: "11.5px", color: "#3a3a5c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "130px" }}>{o.company || "—"}</TD>
                <TD><StagePill stage={o.stage} sm /></TD>
                <TD><PriorityDot o={o} /></TD>
                <TD style={{ fontSize: "12.5px", fontWeight: "700", color: "#1a1a2e" }}>{fmt(o.amount)}</TD>
                <TD><OwnerChip o={o} /></TD>
                <TD><TagsChips o={o} /></TD>
                <TD style={{ fontSize: "11.5px", color: "#6e6e80" }}>{o.source || "—"}</TD>
                <TD style={{ fontSize: "11.5px", color: "#6e6e80" }}>{o.endDate || "—"}</TD>
                {cols.includes("Repeat Customer") && <TD style={{ textAlign: "center" }}>{o.repeatCustomer ? "✅" : "❌"}</TD>}
                {cols.includes("Best Contact Method") && <TD style={{ fontSize: "11px", color: "#6e6e80" }}>{o.contactMethod || "—"}</TD>}
                <TD onClick={e => e.stopPropagation()}>
                  <Dropdown right
                    trigger={<button style={{ width: "22px", height: "22px", border: "none", background: hov === o.id ? "#f0f0f8" : "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#b0b0c8", borderRadius: "5px" }}><Ic n="moreV" s={11} /></button>}
                    items={[
                      { label: "View", icon: "user", onClick: () => onRowClick(o) },
                      ...(!o.archived
                        ? [{ label: "Edit", icon: "edit", onClick: () => onEdit?.(o) }, "---", { label: "Archive", icon: "archive", onClick: () => onArchive?.(o.id) }]
                        : ["---", { label: "Restore", icon: "restore", onClick: () => onRestore?.(o) }]
                      ),
                      { label: "Delete", icon: "trash", onClick: () => onDelete?.(o.id), danger: true },
                    ]}
                  />
                </TD>
              </tr>
            ))
          }
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════
export default function CrmList({ opps, allOpps, activeTab, onRowClick, onEdit, onDelete, onArchive, onRestore }) {
  const [visibleCols, setVisibleCols] = useState([
    "Name","Stage","Priority","Budget","Prob.","Owner","Tags","Source","Close Date","Repeat Customer","Best Contact Method"
  ]);
  const [showSettings, setShowSettings] = useState(false);

  return (
    <div style={{ padding: "12px 16px", height: "calc(100vh - 140px)", overflowY: "auto" }}>

      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <SummaryBar opps={allOpps || opps} activeTab={activeTab} />
        <div style={{ display: "flex", gap: "6px", alignSelf: "flex-start" }}>
          {/* Export CSV */}
          <button onClick={() => exportCSV(opps)}
            title="Export CSV"
            style={{ display: "flex", alignItems: "center", gap: "5px", height: "30px", padding: "0 12px", border: "1.5px solid #e0e0e8", borderRadius: "8px", background: "#fff", cursor: "pointer", fontSize: "11.5px", fontWeight: "600", color: "#3a3a5c" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#3761E9"; e.currentTarget.style.color = "#3761E9"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e8"; e.currentTarget.style.color = "#3a3a5c"; }}>
            <Ic n="import" s={12} /> Export CSV
          </button>
          {/* List Settings */}
          <button onClick={() => setShowSettings(true)}
            title="List Settings"
            style={{ display: "flex", alignItems: "center", gap: "5px", height: "30px", padding: "0 12px", border: "1.5px solid #e0e0e8", borderRadius: "8px", background: "#fff", cursor: "pointer", fontSize: "11.5px", fontWeight: "600", color: "#3a3a5c" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#3761E9"; e.currentTarget.style.color = "#3761E9"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e8"; e.currentTarget.style.color = "#3a3a5c"; }}>
            <Ic n="settings" s={12} /> Settings
          </button>
        </div>
      </div>

      {activeTab === "active"   && <ActiveTab   opps={opps}    cols={visibleCols} onRowClick={onRowClick} onEdit={onEdit} onDelete={onDelete} onArchive={onArchive} />}
      {activeTab === "archived" && <ArchivedTab opps={opps}    onRowClick={onRowClick} onRestore={onRestore} onDelete={onDelete} />}
      {activeTab === "all"      && <AllTab      opps={opps}    cols={visibleCols} onRowClick={onRowClick} onEdit={onEdit} onDelete={onDelete} onArchive={onArchive} onRestore={onRestore} />}

      <ListSettingsPanel visible={showSettings} cols={visibleCols} setCols={setVisibleCols} onClose={() => setShowSettings(false)} />
    </div>
  );
}
